import { fetchReportLists } from "modules/pvs_activities/redux/reportSlice";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import FormTableListModal from "../editor/modals/FormTableListModal";
import { Autocomplete, TextField } from "@mui/material";
import { FaCaretDown } from "react-icons/fa";
import { useLazyGetFormDataVariablesQuery } from "modules/pvs_activities/redux/service/formData";
import { processVariables } from "modules/pvs_activities/helper.general";

const defaultProps = {
    plain: false,
    disabled: false,
    required: false,
    canAdd: false,
    multiple: false,
    selectAll: false,
    listID: 0,
    size: "medium",
    label: "Select",
    defaultOptionLabel: "Select",
    templateId: "",
    value: "",
    onChange: () => {},
    hasOptions: false,
    options: [],
    sx: {},
    marginTop: "5px",
    className: "",
    processVariables: false,
    missionId: 0,
};

const DropdownField = (rawProps) => {
    const props = {...defaultProps, ...rawProps};
    const dispatch = useDispatch();
    const reportLists = useSelector((state) => state.report.lists);
    const [triggerVariables, variablesRes] = useLazyGetFormDataVariablesQuery();

    const handleOnChange = (val) => {
        if (props.plain) {
            if (props.multiple) {
                if (props.selectAll) {
                    if (val.findIndex((r) => r.value === "select-all") !== -1) {
                        props.onChange(
                            finalOptionsFromList.filter((r) => r.value !== "select-all").map((r) => r?.value ?? ""),
                            finalOptionsFromList
                        );
                    } else if (val.findIndex((r) => r.value === "deselect-all") !== -1) {
                        props.onChange([], []);
                    } else {
                        props.onChange(
                            val.map((r) => r?.value ?? ""),
                            val
                        );
                    }
                } else {
                    props.onChange(
                        val.map((r) => r?.value ?? ""),
                        val
                    );
                }
            } else {
                props.onChange(val?.value ?? "", val);
            }
        } else {
            if (props.multiple) {
                if (props.selectAll) {
                    if (val.findIndex((r) => r.value === "select-all") !== -1) {
                        props.onChange(
                            finalOptionsFromList
                                .filter((r) => r.value !== "select-all")
                                .map((r) => generateOption(r?.value ?? "", props.listID, r?.id ?? "")),
                            finalOptionsFromList.filter((r) => r.value !== "select-all")
                        );
                    } else if (val.findIndex((r) => r.value === "deselect-all") !== -1) {
                        props.onChange([], []);
                    } else {
                        props.onChange(
                            val.map((r) => generateOption(r?.value ?? "", props.listID, r?.id ?? "")),
                            val
                        );
                    }
                } else {
                    props.onChange(
                        val.map((r) => generateOption(r?.value ?? "", props.listID, r?.id ?? "")),
                        val
                    );
                }
            } else {
                props.onChange(generateOption(val?.value ?? "", props.listID, val?.id ?? ""), val);
            }
        }
    };

    const generateOption = (content = "", listId = "", listElementId = "") => {
        return {
            content,
            list_id: listId,
            list_element_id: listElementId,
        };
    };
        
    const processVariablesLocal = useCallback(
        (val) => {
            if (props.processVariables && variablesRes.isSuccess) return processVariables(variablesRes, val ?? "");
            return val;
        },
        [variablesRes, props.processVariables]
    );

    const finalOptionsFromList = useMemo(() => {
        let options = [];
        if (!props.multiple && props.defaultOptionLabel && props.defaultOptionLabel !== "Select") {
            options.push({
                value: "",
                label: props.defaultOptionLabel,
                id: "",
                list_id: props.listID,
                list_element_id: "",
            });
        }
        if (props.multiple && props.selectAll) {
            options.push({
                value: props.value.length === props.options.length ? "deselect-all" : "select-all",
                label: props.value.length === props.options.length ? "Deselect All" : "Select All",
                id: "",
                list_id: props.listID,
                list_element_id: "",
            });
        }
        if (props.hasOptions) return [...options, ...props.options];
        let listObj = reportLists?.find((r) => String(r.id) === String(props.listID));
        if (![undefined, null].includes(listObj)) {
            for (let x in listObj.relationElements) {
                options.push({
                    value: processVariablesLocal(listObj.relationElements[x].value),
                    label: processVariablesLocal(listObj.relationElements[x].value),
                    id: listObj.relationElements[x].id,
                    list_id: props.listID,
                    list_element_id: listObj.relationElements[x].id,
                });
            }
        }
        return options;
    }, [
        props.listID,
        props.hasOptions,
        props.options,
        props.multiple,
        props.selectAll,
        reportLists,
        props.value,
        props.defaultOptionLabel,
        variablesRes
    ]);
    const finalValue = useMemo(() => {
        if (processVariablesLocal(props.value) == "") {
            return props.multiple ? [] : null;
        }
        const val = props.plain ? processVariablesLocal(props.value) : props.value?.content;
        if (props.multiple) {
            return finalOptionsFromList.filter((r) => val.includes(r.value)) ?? [];
        } else {
            return finalOptionsFromList.find((r) => String(r.value) === String(val)) ?? null;
        }
    }, [props.value, props.multiple, props.plain, finalOptionsFromList]);

    useEffect(() => {
        if (props.processVariables && ![undefined, null, "", 0, "0"].includes(props.missionId)) {
            triggerVariables(props.missionId, true);
        }
    }, [props.missionId, props.processVariables]);

    return (
        <>
            <Autocomplete
                disablePortal
                required={props.required}
                options={finalOptionsFromList}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={props.label ? props.label : props.defaultOptionLabel}
                        required={props.required}
                    />
                )}
                renderOption={(props, option) => {
                    return (
                        <li {...props} key={option.value}>
                            {option.label}
                        </li>
                    );
                }}
                multiple={props.multiple ? true : false}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                value={finalValue}
                onChange={(e, val) => handleOnChange(val, e)}
                disabled={props.disabled}
                popupIcon={<FaCaretDown />}
                sx={{
                    "& .MuiFormLabel-root, & .MuiFormLabel-root.Mui-disabled": {
                        color: "#252525 !important",
                    },
                    "& fieldset": {
                        borderRadius: 0,
                    },
                    minWidth: 200,
                    marginTop: props.marginTop,
                    ...props.sx,
                }}
                size={props.size}
                className={props.className}
                slotProps={{
                    popper: {
                        sx: {
                            zIndex: 99999999999,
                        },
                    },
                }}
            />
            {[1, true].includes(props.canAdd) && (
                <div className="mt-1">
                    <FormTableListModal
                        showDropdown={false}
                        listNameEditable={false}
                        templateID={props.templateId}
                        value={props.listID}
                        onListUpdate={() => dispatch(fetchReportLists({ mode: "template", id: props.templateId }))}
                    />
                </div>
            )}
        </>
    );
};

export default DropdownField;
